import clsx from "clsx";
import { Search, X } from "lucide-react";
import type { InputHTMLAttributes, KeyboardEvent } from "react";
import { useRef } from "react";
import { Button } from "../button/button";
import { motion } from "motion/react";
import { _ } from "i18n";

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "placeholder"> {
	value: string;
	onChange: (value: string) => void;
	onSearch?: () => void;
	placeholder?: string;
	isLoading: boolean;
	hasSubmitButton?: boolean;
}

export function SearchInput({
	value,
	onChange,
	onSearch,
	isLoading,
	placeholder = _("label.search"),
	className,
	disabled,
	onKeyDown,
	hasSubmitButton = true,
	...inputProps
}: SearchInputProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleClear = () => {
		onChange("");
		inputRef.current?.focus();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		onKeyDown?.(e);
		if (e.defaultPrevented) return;

		if (e.key === "Escape" && value.length > 0) {
			e.preventDefault();
			handleClear();
		}
	};

	return (
		<div className="flex items-center gap-2">
			<motion.div
				layout="size"
				className={clsx(
					"min-w-[200px] px-[12px] py-[8px] flex gap-2 items-center mx-auto rounded-Border-radius-md border border-Border-primary border-solid transition-colors duration-200",
					"hover:border-Border-action-primary hover:text-Border-action-primary",
					disabled ? "pointer-events-none text-Text-disabled bg-Surface-disabled" : "text-Text-primary bg-Surface-primary",
					className
				)}
			>
				<motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
					<Search size={18} className="text-Text-tertiary" />
				</motion.div>
				<input
					ref={inputRef}
					type="text"
					placeholder={placeholder}
					value={value}
					disabled={disabled}
					onChange={(e) => {
						onChange(e.target.value);
					}}
					onKeyDown={handleKeyDown}
					className="text-Text-primary bg-Surface-primary outline-none flex-[1_0_0] placeholder:text-Text-disabled placeholder:font-light w-full disabled:bg-Surface-disabled"
					autoComplete="off"
					{...inputProps}
				/>

				{!disabled && (
					<motion.button
						initial={false}
						animate={{ opacity: value.length > 0 ? 1 : 0 }}
						transition={{ duration: 0.2 }}
						type="button"
						onClick={handleClear}
						aria-label="Clear search"
						className="ml-[6px] flex items-center justify-center cursor-pointer text-Text-tertiary hover:text-Text-primary transition-colors"
					>
						<X size={16} />
					</motion.button>
				)}
			</motion.div>
			{hasSubmitButton && (
				<motion.div initial={false} className="h-[40px]" animate={{ opacity: value.length > 0 ? 1 : 0 }} transition={{ duration: 0.2 }}>
					<Button
						className="h-full w-10"
						variant="secondary"
						size="small"
						text={_("label.search")}
						iconLeft={<Search size={16} />}
						onClick={onSearch}
						aria-label={_("label.search")}
						loading={isLoading}
						disabled={isLoading || disabled || value.length === 0}
					/>
				</motion.div>
			)}
		</div>
	);
}
